<template>
  <main>
    <v-container>
      <v-row>
        <v-col class="px-4 py-2 d-flex align-center">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
        </v-col>
        <v-col class="justify-end px-4 py-2 d-flex align-center">
          <router-link :to="redirectToEditView($route.params.id)" class="mr-4">
            <v-btn color="primary" link depressed>Edit</v-btn>
          </router-link>
          <v-btn
            :loading="checkResourceIsDeleting($route.params.id)"
            color="error"
            link
            depressed
            @click="showDeleteModal()"
            >Delete</v-btn
          >
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <!-- Tabs -->
            <v-tabs v-model="tab" background-color="primary" dark show-arrows>
              <v-tab>Details</v-tab>
              <v-tab>Fields</v-tab>
              <v-tab>Page Types</v-tab>
            </v-tabs>

            <!-- Tab Items -->
            <v-tabs-items v-model="tab">
              <v-tab-item :transition="false" :reverse-transition="false">
                <v-list dense>
                  <template v-for="(item, index) in resourceItems">
                    <div :key="index">
                      <v-list-item>
                        <v-list-item-content class="font-weight-medium">{{ item.title }}</v-list-item-content>
                        <v-list-item-content v-if="item.type === 'text'" class="align-end">
                          {{ item.value }}
                        </v-list-item-content>
                        <v-list-item-content v-if="item.type === 'tick'" class="align-end">
                          <tick-or-cross :value="!!item.value" class="justify-start" />
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider />
                    </div>
                  </template>
                  <v-list-item>
                    <v-list-item-content class="font-weight-medium">Module count</v-list-item-content>
                    <v-list-item-content>
                      {{ moduleCount }}
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false">
                <v-list dense>
                  <template v-for="(item, index) in resource.fields">
                    <div :key="index">
                      <v-list-item>
                        <v-list-item-content class="font-weight-medium">
                          <div>
                            <span class="mr-2 text-capitalize">{{ item.title }}</span>
                            <v-chip color="primary" class="mr-2  text-capitalize">{{ item.type }} Field</v-chip>
                            <v-chip color="primary" class="mr-2">{{ item.key }}</v-chip>
                            <span v-if="item.required" class="red--text">*</span>
                          </div>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider v-if="index !== resource.fields.length - 1" />
                    </div>
                  </template>
                </v-list>
              </v-tab-item>
              <v-tab-item :transition="false" :reverse-transition="false">
                <v-list>
                  <v-list-item
                    v-for="(pageType, index) of modulePageTypes"
                    :key="index"
                    :to="{ name: 'page-types.manage', params: { id: pageType.id } }"
                  >
                    <v-list-item-icon>
                      <v-chip color="primary" small v-text="pageType.modules.length" />
                    </v-list-item-icon>

                    <v-list-item-content>
                      <v-list-item-title v-text="pageType.name" />
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <delete-modal
      v-if="deleteModalVisible"
      v-model="deleteModalVisible"
      :is-disabled="isDeleting"
      :is-loading="isDeleting"
      :item="deletingResource"
      :category="resourceType"
      @modal-confirm="onResourceDeleteConfirmed"
      @modal-cancel="onResourceDeleteCancelled"
    />
  </main>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import PermissionsHandler from '@/mixins/permissions-handler'
import ViewResource from '@/mixins/view-resource'
import DeleteResource from '@/mixins/delete-resource'

export default {
  name: 'ModulesView',
  mixins: [PermissionsHandler, ViewResource, DeleteResource],
  data() {
    return {
      resourceType: 'Module',
      resourcePrefix: 'modules',
      resourceKeys: [
        { title: 'ID', key: 'id' },
        { title: 'Name', key: 'name' },
        { title: 'Title', key: 'title' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      resource: 'modules/getModule',
      pageTypes: 'pageTypes/getPageTypes',
    }),
    availablePageTypes() {
      return this.pageTypes.data
        .map(({ id, name, schema: { sections } }) => ({ id, name, modules: Object.values(sections).flat() }))
        .filter(({ modules }) => modules.filter(({ id }) => this.resource.id === id).length)
    },
    modulePageTypes() {
      return this.availablePageTypes.map(({ id, name, modules }) => ({
        id,
        name: name.replace(/-/g, ' ').replace(/(^\w{1})|(\s{1}\w{1})/g, match => match.toUpperCase()),
        modules: modules.filter(({ id }) => this.resource.id === id),
      }))
    },
    moduleCount() {
      return this.modulePageTypes.flatMap(({ modules }) => modules).length
    },
  },
  mounted() {
    this.fetchPageTypes({ paginate: { perPage: null } })
  },
  methods: {
    ...mapActions({
      findResource: 'modules/find',
      deleteResource: 'modules/delete',
      fetchPageTypes: 'pageTypes/fetch',
    }),
  },
}
</script>
